import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleLayout, { blogStyles as s } from './BlogArticleLayout';

function BlogArticleQuiEstConcerneNIS2() {
  return (
    <BlogArticleLayout
      tag="NIS2"
      title="Comment savoir si mon entreprise est concernée par NIS2 ?"
      date="10 septembre 2026"
      ctaTitle="Vérifiez si NIS2 s'applique à votre entreprise"
      ctaText="Le diagnostic gratuit intègre les critères NIS2 et vous indique clairement si votre entreprise entre dans le champ d'application."
    >
      <p style={s.intro}>
        NIS2 est souvent perçue comme une directive réservée aux grands groupes ou aux opérateurs
        d'infrastructures critiques. En réalité, son périmètre est plus large qu'on ne le pense, et de
        nombreuses PME et ETI sont concernées sans le savoir — directement, ou indirectement via leurs
        clients. Voici comment évaluer votre situation.
      </p>

      <h2 style={s.h2}>NIS2, en une phrase</h2>
      <p>
        NIS2 est une directive européenne qui impose des obligations de cybersécurité aux entités jugées
        importantes pour le fonctionnement de l'économie et de la société. Pour une présentation plus large,
        replacée face au RGPD et à ISO 27001, voir notre article
        {' '}<Link to="/blog/rgpd-nis2-iso27001-differences" style={s.link}>RGPD, NIS2, ISO 27001 : quelles différences ?</Link>
      </p>

      <h2 style={s.h2}>Les deux catégories : entités essentielles et entités importantes</h2>
      <p>
        NIS2 distingue deux niveaux d'entités, avec des obligations et un niveau de contrôle différents :
        les <strong>entités essentielles</strong>, dans les secteurs jugés les plus critiques (énergie, santé,
        transport, banque, eau, infrastructures numériques...), et les <strong>entités importantes</strong>,
        dans des secteurs également sensibles mais avec un régime de supervision allégé. Le classement dépend
        à la fois du secteur d'activité et de la taille de l'entreprise.
      </p>

      <h2 style={s.h2}>Les secteurs concernés</h2>
      <p>La directive couvre un périmètre sectoriel large, parmi lesquels :</p>
      <ul style={s.ul}>
        <li style={s.li}>Énergie (électricité, gaz, pétrole, hydrogène)</li>
        <li style={s.li}>Santé (établissements de santé, laboratoires, fabricants de dispositifs médicaux)</li>
        <li style={s.li}>Transport (aérien, ferroviaire, maritime, routier)</li>
        <li style={s.li}>Banque et infrastructures des marchés financiers</li>
        <li style={s.li}>Eau potable et eaux usées</li>
        <li style={s.li}>Infrastructures numériques (hébergeurs, fournisseurs cloud, data centers, télécoms)</li>
        <li style={s.li}>Administration publique</li>
        <li style={s.li}>Espace, gestion des déchets, industrie chimique, agroalimentaire, fabrication industrielle</li>
      </ul>
      <p>
        Cette liste est indicative : le classement précis dépend de sous-catégories définies par le texte et
        sa transposition en droit français.
      </p>

      <h2 style={s.h2}>Les critères de taille</h2>
      <p>
        Au-delà du secteur, NIS2 s'adresse en priorité aux moyennes et grandes entreprises de ces secteurs.
        Les seuils précis (effectifs, chiffre d'affaires) relèvent de la transposition française du texte et
        peuvent évoluer : plutôt que de se fier à un chiffre approximatif, mieux vaut vérifier votre situation
        au cas par cas, notamment si votre entreprise se situe dans une zone intermédiaire.
      </p>

      <h2 style={s.h2}>Et si vous êtes sous-traitant d'une entreprise concernée ?</h2>
      <p>
        Même si votre entreprise n'entre pas directement dans le champ d'application de NIS2, vous pouvez
        être concerné indirectement : les entités essentielles et importantes sont incitées, voire tenues, de
        s'assurer que leurs prestataires et sous-traitants respectent également un niveau de sécurité
        suffisant. En pratique, cela se traduit par des exigences contractuelles répercutées sur toute la
        chaîne — un donneur d'ordre soumis à NIS2 peut ainsi demander à ses fournisseurs des garanties
        proches de celles qu'il doit lui-même respecter.
      </p>

      <h2 style={s.h2}>Que faire si vous pensez être concerné</h2>
      <p>
        La priorité est de clarifier votre statut exact (secteur, taille, catégorie), puis d'évaluer vos
        écarts par rapport aux exigences de la directive : gestion des risques, sécurité de la chaîne
        d'approvisionnement, gestion des incidents, plans de continuité. Un diagnostic structuré permet de
        faire ce point rapidement plutôt que de partir d'une feuille blanche.
      </p>

      <h2 style={s.h2}>Et si vous n'êtes pas concerné par NIS2 ?</h2>
      <p>
        Cela ne signifie pas que votre entreprise n'a aucune obligation : le RGPD continue de s'appliquer dès
        que vous traitez des données personnelles, et les bonnes pratiques de cybersécurité restent
        pertinentes indépendamment de tout texte réglementaire. Notre
        {' '}<Link to="/blog/cybersecurite-pme-guide-dirigeants" style={s.link}>guide cybersécurité PME</Link>{' '}
        couvre ces fondamentaux, utiles à toute entreprise quel que soit son secteur.
      </p>

      <p style={s.disclaimer}>
        Cet article a une vocation informative. Le classement précis d'une entreprise au regard de NIS2
        dépend de critères techniques et juridiques propres à chaque situation : en cas de doute, faites
        confirmer votre analyse par un professionnel du droit ou par votre référent conformité.
      </p>
    </BlogArticleLayout>
  );
}

export default BlogArticleQuiEstConcerneNIS2;
